import React, { useRef, useState, useLayoutEffect } from 'react';
import '../styles/ItemTooltip.css';

const SLOT_CLASS = {
    weapon: 'WeaponMod',
    spirit: 'TechMod',
    vitality: 'ArmorMod'
};

const SECTION_TYPE_CLASS = {
    innate: 'AbilityType_Innate',
    active: 'AbilityType_Active',
    passive: 'AbilityType_Passive'
};

// Deadlock CSS variable names → actual hex colors
// The API returns these as fill/color values in loc_string HTML
const DEADLOCK_COLORS = {
    armorColor:           '#00FF9a',
    spiritBrightColor:    '#CE91FF',
    courageBrightColor:   '#EC981a',
    colorEnemy:           '#FF410D',
    shardColor:           '#9aFFD6',
    fortitudeBrightColor: '#7CBB1E',
    offWhite:             '#FFEFD7',
    white:                '#FFEFD7',
};

// Map inline-attribute-label class names to hex colors (for fill="white" SVGs)
const LABEL_CLASS_COLORS = {
    Heal: '#00FF9a', Heals: '#00FF9a', Healing: '#00FF9a', Regen: '#00FF9a',
    MaxHealth: '#00FF9a', Health: '#00FF9a',
    MoveSpeed: '#00FF9a', BonusMoveSpeed: '#00FF9a', BonusSprintSpeed: '#00FF9a',
    FireRate: '#EC981a', BonusFireRate: '#EC981a', ReducedFireRate: '#EC981a',
    WeaponDamage: '#EC981a', BonusWeaponDamage: '#EC981a', BulletDamage: '#EC981a',
    MeleeDamage: '#EC981a', BulletResist: '#EC981a',
    SpiritDamage: '#CE91FF', BonusSpiritDamage: '#CE91FF', SpiritDPS: '#CE91FF',
    Spirit: '#CE91FF', SpiritResist: '#CE91FF', Silence: '#CE91FF',
    Stun: '#CE91FF', Slow: '#CE91FF', Slowing: '#CE91FF',
    Damage: '#FF410D',
};

// Replace Deadlock color variable names in the entire HTML string
const resolveDescriptionColors = (html) => {
    if (!html) return '';
    let result = html;

    // Global replace: fill="colorName" on SVGs/paths
    for (const [name, hex] of Object.entries(DEADLOCK_COLORS)) {
        if (name === 'white' || name === 'offWhite') continue;
        result = result.replaceAll(`fill="${name}"`, `fill="${hex}"`);
    }
    // Remove invalid inline color variable names (browser can't render them)
    // Replace with inherit so text stays normal description color
    for (const name of Object.keys(DEADLOCK_COLORS)) {
        if (name === 'white' || name === 'offWhite') continue;
        result = result.replaceAll(`color: ${name}`, 'color: inherit');
    }

    // For SVGs with fill="white", look at the following label class to determine color
    result = result.replace(
        /(<svg[^>]*fill="white"[^>]*>)([\s\S]*?)(<\/svg>)\s*\n?\s*<span[^>]*class="inline-attribute-label\s+(\w+)"[^>]*>/g,
        (match, svgOpen, inner, svgClose, labelClass) => {
            const hex = LABEL_CLASS_COLORS[labelClass];
            if (!hex) return match;
            const newOpen = svgOpen.replace('fill="white"', `fill="${hex}"`);
            const newInner = inner.replaceAll('fill="white"', `fill="${hex}"`);
            return match.replace(svgOpen, newOpen).replace(inner, newInner);
        }
    );

    // Replace keybinding placeholders like {g:citadel_binding:'Reload'} with a key icon
    result = result.replace(
        /\{g:citadel_binding:'([^']+)'\}/g,
        (_, key) => {
            const KEY_MAP = {
                Reload: 'R',
                Parry: 'F',
                Jump: 'Space',
                Crouch: 'Ctrl',
                Dash: 'Shift',
            };
            const display = KEY_MAP[key] || key;
            return `<span class="keybind-icon">${display}</span>`;
        }
    );

    return result;
};

// Fallback css_class for properties that have empty css_class from API
// Maps property key name patterns to css_class values
const PROPERTY_CSS_FALLBACKS = {
    BonusHealth:            'health',
    BonusFireRate:          'fire_rate',
    FireRateSlow:           'fire_rate',
    BuffDuration:           'duration',
    BonusShotsDuration:     'duration',
    DebuffDuration:         'duration',
    RegenDuration:          'duration',
    SummonDuration:         'duration',
    ImmunityDuration:       'duration',
    BarrierDuration:        'duration',
    ResistReductionDuration:'duration',
    BulletResistDuration:   'duration',
    WeaponDamageBonusDuration:'duration',
    DotDuration:            'duration',
    EMPDuration:            'duration',
    StunDuration:           'stun',
    StunDelay:              'stun',
    SilenceDuration:        'silence',
    FreezeDuration:         'stun',
    SlowPercent:            'slow',
    BonusMovespeed:         'move_speed',
    BonusSprintSpeed:       'move_speed',
    BaseAttackDamagePercent:'bullet_damage',
    WeaponPower:            'bullet_damage',
    BonusClipSize:          'clipsize',
    BonusClipSizePercent:   'clipsize',
    BonusReloadSpeed:       'reload_speed',
    BulletResist:           'bullet_armor_up',
    BulletResistPerStack:   'bullet_armor_up',
    MeleeResist:            'bullet_armor_up',
    TechPower:              'tech_power',
    TechPowerPercent:       'tech_power',
    TechResist:             'tech_armor_up',
    Radius:                 'distance',
    ExplosionRadius:        'distance',
    DebuffRadius:           'distance',
    BulletRadius:           'distance',
    SpottedRadius:          'distance',
    Damage:                 'damage',
    DPS:                    'tech_damage',
    HealFromHero:           'healing',
    HealFromNPC:            'healing',
    HealLifePercent:        'healing',
    HealAmpCastPercent:     'healing',
    HealAmpReceivePenaltyPercent: 'damage',
    HealthDrainedPerSecond: 'healing',
    Stamina:                'move_speed',
    StaminaCooldownReduction:'move_speed',
    CooldownReductionFlat:  'cooldown',
    ProcCooldown:           'cooldown',
    AmmoPerSoul:            'souls',
    MeleeDistanceScale:     'melee_damage',
    BonusBulletSpeedPercent:'bullet_damage',
};

// Scaling indicator info for stats that scale with spirit, weapon, or boons
const SCALE_TYPE_INFO = {
    ETechPower:    { icon: './icons/properties/icon_spirit.svg',    color: '#CE91FF', label: 'Spirit' },
    EWeaponPower:  { icon: './icons/properties/icon_courage.svg',   color: '#EC981a', label: 'Weapon' },
    ELevelUpBoons: { icon: './icons/properties/20px-Boon_scaling.png', color: '#7CBB1E', label: 'Boons', isColor: true },
};

// Returns scaling info for a property, or null if it doesn't scale
const getScaleInfo = (prop) => {
    const sf = prop?.scale_function;
    if (!sf) return null;
    const info = SCALE_TYPE_INFO[sf.specific_stat_scale_type];
    if (!info) return null;
    return { ...info, multiplier: sf.stat_scale };
};

// Compute adjusted buildup value based on hero weapon data
// Returns the adjusted percentage string, or null if not applicable
const computeBuildupValue = (prop, weaponData) => {
    if (!prop || !weaponData) return null;
    const sf = prop.scale_function;
    if (!sf || sf.subclass_name !== 'buildup_scale_function') return null;

    const rawValue = parseFloat(prop.value);
    if (!rawValue || !isFinite(rawValue)) return null;

    let cycleTime = weaponData.cycleTime;
    if (!cycleTime || cycleTime <= 0) return null;

    // If maxSpinCycleTime exists, use it instead
    if (weaponData.maxSpinCycleTime > 0) {
        cycleTime = weaponData.maxSpinCycleTime;
    }

    // If burst weapon, adjust cycle time
    const burstCount = weaponData.burstShotCount || 1;
    const intraBurst = weaponData.intraBurstCycleTime || 0;
    if (intraBurst && burstCount > 1) {
        cycleTime += burstCount * intraBurst;
        cycleTime = cycleTime / burstCount;
    }

    const adjustedCycleTime = cycleTime / (1 + cycleTime);
    const displayValue = (adjustedCycleTime / rawValue) * 100;

    return displayValue.toFixed(1);
};

// Resolve the css_class for a property, using API value or fallback
const getPropertyCssClass = (prop, propKey) => {
    if (prop?.css_class) return prop.css_class;
    // Exact match
    if (PROPERTY_CSS_FALLBACKS[propKey]) return PROPERTY_CSS_FALLBACKS[propKey];
    // Partial match
    const lowerKey = propKey?.toLowerCase() || '';
    for (const [pattern, cssClass] of Object.entries(PROPERTY_CSS_FALLBACKS)) {
        if (lowerKey.includes(pattern.toLowerCase())) return cssClass;
    }
    return '';
};

const ItemTooltip = ({ item, x, y, heroId, heroCache, weaponCache }) => {
    const tooltipRef = useRef(null);
    const [dims, setDims] = useState({ width: 0, height: 0 });

    useLayoutEffect(() => {
        if (tooltipRef.current) {
            const rect = tooltipRef.current.getBoundingClientRect();
            setDims({ width: rect.width, height: rect.height });
        }
    }, [item]);

    if (!item) return null;

    const slotType = item.item_slot_type || '';
    const modClass = SLOT_CLASS[slotType] || 'WeaponMod';
    const properties = item.properties || {};
    const tooltipSections = item.tooltip_sections || [];

    // Check if a property should be shown (not disabled)
    const isPropertyVisible = (key) => {
        const prop = properties[key];
        if (!prop) return false;
        const val = String(prop.value);
        const disableVal = prop.disable_value !== undefined ? String(prop.disable_value) : null;
        if (disableVal !== null && val === disableVal) return false;
        if (val === '0' && !prop.tooltip_section && !prop.tooltip_is_important && !prop.tooltip_is_elevated) return false;
        return true;
    };

    // Resolve weapon data for the current hero
    const heroInfo = heroId && heroCache ? heroCache[heroId] : null;
    const weaponData = heroInfo?.weaponName && weaponCache ? weaponCache[heroInfo.weaponName] : null;

    const formatPropertyValue = (prop, key) => {
        // Check if this property needs buildup adjustment
        const buildupVal = computeBuildupValue(prop, weaponData);
        if (buildupVal !== null) {
            const prefix = prop.prefix ? prop.prefix.replace('{s:sign}', '+') : '';
            const postfix = prop.postfix || '';
            return `${prefix}${buildupVal}${postfix}`;
        }

        let val = String(prop.value || '');
        const prefix = prop.prefix ? prop.prefix.replace('{s:sign}', '+') : '';
        let postfix = prop.postfix || '';

        // API often returns values like "2m" with postfix "m", causing "2mm"
        // Strip trailing unit from value if postfix already provides it
        if (postfix && val.endsWith(postfix.trim())) {
            val = val.slice(0, -postfix.trim().length);
        }

        return `${prefix}${val}${postfix}`;
    };

    // Tooltip positioning — measure after render, then clamp to viewport
    const pad = 8;
    const offsetX = 16;
    const offsetY = 16;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const measured = dims.width > 0 && dims.height > 0;

    const tooltipStyle = { position: 'fixed', zIndex: 10000 };

    if (!measured) {
        // First render: place off-screen invisibly so we can measure
        tooltipStyle.left = '-9999px';
        tooltipStyle.top = '-9999px';
        tooltipStyle.visibility = 'hidden';
    } else {
        const w = dims.width;
        const h = dims.height;

        // Horizontal: prefer right of cursor, flip left if overflows
        let left = x + offsetX;
        if (left + w > vw - pad) left = x - w - offsetX;
        left = Math.max(pad, Math.min(left, vw - w - pad));

        // Vertical: prefer above cursor (bottom-anchored), flip below if overflows
        let top = y - offsetY - h;
        if (top < pad) {
            top = y + offsetY; // below cursor
        }
        top = Math.max(pad, Math.min(top, vh - h - pad));

        tooltipStyle.left = `${left}px`;
        tooltipStyle.top = `${top}px`;
    }

    // Render icon + value for an important stat box
    const renderStatIcon = (prop, key) => {
        const cssClass = getPropertyCssClass(prop, key);
        const propClass = cssClass ? `prop_${cssClass}` : 'HideImportantStatImage';
        const scaleInfo = getScaleInfo(prop);
        return (
            <div className={`ImportantStatContent ${propClass}`}>
                <div className={`ImportantStatImageValueContainer ${!cssClass ? 'HideImportantStatImage' : ''}`}>
                    <div className="ImportantStatImage" />
                    <span className="ImportantStatValue">
                        {formatPropertyValue(prop, key)}
                    </span>
                </div>
                <span className="ImportantStatLabel">
                    {prop.label || key}
                </span>
                {scaleInfo && (
                    <div className="ScalingBadge">
                        <div
                            className="ScalingBadgeIcon"
                            style={scaleInfo.isColor
                                ? { backgroundImage: `url('${scaleInfo.icon}')` }
                                : {
                                    maskImage: `url('${scaleInfo.icon}')`,
                                    WebkitMaskImage: `url('${scaleInfo.icon}')`,
                                    backgroundColor: scaleInfo.color,
                                }
                            }
                        />
                        <span className="ScalingBadgeLabel" style={{ color: scaleInfo.color }}>
                            x{scaleInfo.multiplier}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    // Render icon for a basic property row
    const renderBasicPropIcon = (prop, key) => {
        const cssClass = getPropertyCssClass(prop, key);
        if (!cssClass) return null;
        return <div className={`BasicPropertyIcon prop_${cssClass}`}><div className="ImportantStatImage" /></div>;
    };

    // Render a single tooltip section based on tooltip_sections data
    const renderTooltipSection = (section, sectionIndex) => {
        const sectionType = section.section_type || 'passive';
        const sectionClass = SECTION_TYPE_CLASS[sectionType] || 'AbilityType_Passive';
        const attrs = section.section_attributes || [];

        // Get cooldown for active/passive header
        const cooldownProp = properties.AbilityCooldown;
        const cooldownVal = cooldownProp?.value || '0';
        const cooldownDisable = cooldownProp?.disable_value !== undefined ? String(cooldownProp.disable_value) : '0';
        const hasCooldown = sectionType !== 'innate' && cooldownVal !== '0' && cooldownVal !== cooldownDisable;

        return (
            <div key={sectionIndex} className={`AppliedAttributes ${sectionClass}`}>
                {/* Section header bar (Active/Passive label + cooldown) - hidden for innate */}
                <div className={`CooldownHeader ${hasCooldown ? 'hasCooldown' : ''}`}>
                    <span className="ActivePassiveLabel">
                        {sectionType === 'active' ? 'Active' : 'Passive'}
                    </span>
                    <div className="HeaderAttributesContainer">
                        {hasCooldown && (
                            <div className="CooldownContainer">
                                <div className="CooldownImage" />
                                <span className="CooldownLabel">
                                    {cooldownVal}{cooldownProp?.postfix || 's'}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Section content */}
                {attrs.map((attr, attrIndex) => {
                    const elevatedProps = (attr.elevated_properties || []).filter(k => isPropertyVisible(k));
                    const regularProps = (attr.properties || []).filter(k => isPropertyVisible(k));
                    const importantProps = (attr.important_properties || []).filter(k => isPropertyVisible(k));
                    const locString = attr.loc_string || '';

                    return (
                        <div key={attrIndex} className="AbilityTypeSection">
                            <div className="AppliedAttributesContainer">
                                {/* Elevated properties (bold stat lines) */}
                                {elevatedProps.map(key => {
                                    const prop = properties[key];
                                    return (
                                        <div key={key} className="ElevatedProperty">
                                            <span className="ElevatedPropertyValue">{formatPropertyValue(prop, key)}</span>
                                            <span className="ElevatedPropertyLabel"> {prop.label || key}</span>
                                        </div>
                                    );
                                })}

                                {/* Regular properties (innate stat lines) */}
                                {sectionType === 'innate' && regularProps.map(key => {
                                    const prop = properties[key];
                                    return (
                                        <div key={key} className="InnateProperty">
                                            <span className="InnatePropertyValue">{formatPropertyValue(prop, key)}</span>
                                            <span className="InnatePropertyLabel"> {prop.label || key}</span>
                                        </div>
                                    );
                                })}

                                {/* Description text */}
                                {locString && (
                                    <div
                                        className="ModInfoLabel"
                                        dangerouslySetInnerHTML={{ __html: resolveDescriptionColors(locString) }}
                                    />
                                )}

                                {/* Important properties (stat boxes) */}
                                {importantProps.length > 0 && (
                                    <div className={`StatsAppliedBackground ${importantProps.length >= 2 ? 'HasMultipleImportantProperties' : ''}`}>
                                        {importantProps.map(key => {
                                            const prop = properties[key];
                                            return (
                                                <div key={key} className="ImportantAttribute">
                                                    <div className="ImportantStatBox">
                                                        {renderStatIcon(prop, key)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Regular properties below stat boxes (non-innate sections) */}
                                {sectionType !== 'innate' && regularProps.length > 0 && (
                                    <div className="BasicPropertiesContainer">
                                        {regularProps.map(key => {
                                            const prop = properties[key];
                                            return (
                                                <div key={key} className="BasicProperty">
                                                    {renderBasicPropIcon(prop, key)}
                                                    <span className="BasicPropertyValue">{formatPropertyValue(prop, key)}</span>
                                                    <span className="BasicPropertyLabel"> {prop.label || key}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Fallback: if no tooltip_sections, build from description + properties
    const renderFallback = () => {
        const isActiveItem = item.activation && item.activation !== 'passive';
        const hasDesc = item.description?.desc;
        const hasPassiveDesc = item.description?.passive;
        const hasActiveDesc = item.description?.active;

        const cooldownProp = properties.AbilityCooldown;
        const cooldownVal = cooldownProp?.value || '0';
        const hasCooldown = isActiveItem && cooldownVal !== '0';

        // Collect visible properties that aren't system ones
        const visibleProps = Object.entries(properties).filter(([key, prop]) => {
            if (key === 'AbilityCooldown') return false;
            return isPropertyVisible(key);
        });

        const sectionType = isActiveItem ? 'active' : 'passive';
        const sectionClass = isActiveItem ? 'AbilityType_Active' : 'AbilityType_Passive';
        const descHtml = hasActiveDesc || hasPassiveDesc || hasDesc || '';

        return (
            <div className={`AppliedAttributes ${sectionClass}`}>
                <div className={`CooldownHeader ${hasCooldown ? 'hasCooldown' : ''}`}>
                    <span className="ActivePassiveLabel">
                        {isActiveItem ? 'Active' : 'Passive'}
                    </span>
                    <div className="HeaderAttributesContainer">
                        {hasCooldown && (
                            <div className="CooldownContainer">
                                <div className="CooldownImage" />
                                <span className="CooldownLabel">
                                    {cooldownVal}{cooldownProp?.postfix || 's'}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="AbilityTypeSection">
                    <div className="AppliedAttributesContainer">
                        {descHtml && (
                            <div
                                className="ModInfoLabel"
                                dangerouslySetInnerHTML={{ __html: resolveDescriptionColors(descHtml) }}
                            />
                        )}
                        {visibleProps.length > 0 && (
                            <div className="StatsAppliedBackground">
                                {visibleProps.map(([key, prop]) => (
                                    <div key={key} className="ImportantAttribute">
                                        <div className="ImportantStatBox">
                                            {renderStatIcon(prop, key)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div ref={tooltipRef} className="item-tooltip-wrapper" style={tooltipStyle}>
            <div className="TooltipShadow">
                <div className={`ModTooltipContainer ${modClass}`}>
                    <div className="HeaderContainer">
                        <div className="ModNameContainer">
                            <span className="ModName">{item.name}</span>
                            <div className="ModCostContainer">
                                <div className="goldIcon" />
                                <span className="ModCost">{item.cost}</span>
                            </div>
                        </div>
                    </div>
                    <div className="AttributesContainer">
                        <div className="SectionsContainer">
                            {tooltipSections.length > 0
                                ? tooltipSections.map((section, i) => renderTooltipSection(section, i))
                                : renderFallback()
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ItemTooltip;
